import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Delete, Type } from "lucide-react";

const NUM_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "-", "0"];
const FALLBACK_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const KEY_CLS =
  "h-9 rounded-md border border-slate-200 bg-white text-slate-900 text-base font-semibold flex items-center justify-center transition-colors active:scale-90 active:bg-slate-100 select-none";

/**
 * Mobile-only dedicated keypad for alphanumeric product codes (e.g. A493316060,
 * C93112040-1). Phone-style 3-column numpad with a top row of the letters that
 * actually appear in product codes. Inserts at the caret and keeps focus.
 *
 * Fast typing: instead of reading the (possibly stale) `value` prop on every
 * press, we track the latest value + caret in refs and update them synchronously
 * on each key. This prevents rapid presses from being dropped while React is
 * still propagating the previous onChange. Keys use onPointerDown for instant,
 * reliable response on touch devices + a light haptic tap.
 *
 * Props: inputRef, value, onChange(next), onSwitchToSearch().
 */
export default function CodeKeypad({ inputRef, value, onChange, onSwitchToSearch }) {
  const [letters, setLetters] = useState([]);
  const valueRef = useRef(value || "");
  const posRef = useRef((value || "").length);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await api.get("/products/code-letters");
        if (on) setLetters(Array.isArray(data?.letters) && data.letters.length ? data.letters : FALLBACK_LETTERS);
      } catch (_) {
        if (on) setLetters(FALLBACK_LETTERS);
      }
    })();
    return () => { on = false; };
  }, []);

  // Keep refs in sync when the value changes from outside (e.g. cleared after add).
  useEffect(() => {
    valueRef.current = value || "";
    if (posRef.current > valueRef.current.length) posRef.current = valueRef.current.length;
  }, [value]);

  const tap = () => {
    try { if (navigator.vibrate) navigator.vibrate(8); } catch (_) { /* unsupported */ }
  };

  const apply = (mutator) => {
    const el = inputRef?.current;
    const v = valueRef.current || "";
    let start = posRef.current ?? v.length;
    let end = start;
    // Trust the DOM caret only when the input is in sync with our tracked value,
    // so manual caret moves still work but rapid presses never read a stale caret.
    if (el && el.value === v && typeof el.selectionStart === "number") {
      start = el.selectionStart;
      end = el.selectionEnd;
    }
    start = Math.min(Math.max(start, 0), v.length);
    end = Math.min(Math.max(end, 0), v.length);
    const { next, pos } = mutator(v, start, end);
    valueRef.current = next;
    posRef.current = pos;
    tap();
    onChange(next);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        try { el.setSelectionRange(pos, pos); } catch (_) { /* ignore */ }
      }
    });
  };

  const insert = (ch) => apply((v, s, e) => ({
    next: (v.slice(0, s) + ch + v.slice(e)).toUpperCase(),
    pos: s + ch.length,
  }));

  const backspace = () => apply((v, s, e) => {
    if (s !== e) return { next: (v.slice(0, s) + v.slice(e)).toUpperCase(), pos: s };
    if (s > 0) return { next: (v.slice(0, s - 1) + v.slice(e)).toUpperCase(), pos: s - 1 };
    return { next: v, pos: 0 };
  });

  // onPointerDown fires immediately on touch-down (no click delay / dropped taps)
  // and preventDefault keeps the input focused.
  const press = (e, fn) => { e.preventDefault(); fn(); };

  return (
    <div className="sm:hidden mt-2 rounded-xl border border-slate-200 bg-slate-50 p-1.5" data-testid="code-keypad">
      {/* Letters found in product codes */}
      <div className="flex flex-wrap gap-1 mb-1.5">
        {letters.map((L) => (
          <button
            key={L}
            type="button"
            data-testid={`keypad-letter-${L}`}
            onContextMenu={(e) => e.preventDefault()}
            onPointerDown={(e) => press(e, () => insert(L))}
            className="h-7 min-w-[1.9rem] px-1 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-bold flex items-center justify-center active:bg-indigo-100 active:scale-90 transition-colors select-none"
          >
            {L}
          </button>
        ))}
      </div>

      {/* Phone-style numeric keypad */}
      <div className="grid grid-cols-3 gap-1">
        {NUM_KEYS.map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`keypad-${n}`}
            onContextMenu={(e) => e.preventDefault()}
            onPointerDown={(e) => press(e, () => insert(n))}
            className={KEY_CLS}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          data-testid="keypad-backspace"
          onContextMenu={(e) => e.preventDefault()}
          onPointerDown={(e) => press(e, backspace)}
          aria-label="Sil"
          className="h-9 rounded-md border border-rose-200 bg-rose-50 text-rose-600 flex items-center justify-center active:bg-rose-100 transition-colors active:scale-90 select-none"
        >
          <Delete className="h-5 w-5" />
        </button>
      </div>

      {/* Switch to the system keyboard to search by product name */}
      {onSwitchToSearch && (
        <button
          type="button"
          data-testid="keypad-switch-search"
          onClick={onSwitchToSearch}
          className="mt-1.5 w-full h-8 rounded-md border border-slate-200 bg-white text-slate-600 text-xs font-medium flex items-center justify-center gap-2 active:bg-slate-100 transition-colors"
        >
          <Type className="h-3.5 w-3.5" /> Ürün adıyla ara (klavye)
        </button>
      )}
    </div>
  );
}
